import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { paymentApi } from '../../../api/paymentApi';
import {
  CreateActivityVoucherPayload,
  CreateActivityVoucherResponse,
  IeCategory,
  ProjectBankAccount,
  incomeExpenseApi,
} from '../../../api/incomeExpenseApi';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import { printVoucherHtml } from '../../../utils/voucherPrint';

const formatAmountInput = (value: number) => {
  if (!value && value !== 0) return '';
  return new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(Math.round(value));
};

const parseAmountInput = (value: string) => Number(value.replace(/[^\d]/g, '')) || 0;

const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const BLOCKED_REVENUE_CODES = new Set(['thu_phi', 'thanh_toan_phi', 'thu_tra_truoc']);
const BLOCKED_EXPENDITURE_CODES = new Set(['chi_tra_truoc']);

const formatAccountLabel = (account: {
  name: string;
  accountType?: string | null;
  isDefault?: boolean;
}) => {
  const parts = [account.name];
  if (account.accountType) parts.push(account.accountType);
  if (account.isDefault) parts.push('(Mặc định)');
  return parts.join(' · ');
};

type ModalStep = 'form' | 'preview' | 'success';

const LedgerVoucherPane: React.FC<{
  entryId: string | null;
  previewHtml: string;
  title: string;
}> = ({ entryId, previewHtml, title }) => {
  const [loading, setLoading] = useState(false);
  const [htmlContent, setHtmlContent] = useState(previewHtml);

  useEffect(() => {
    if (!entryId) {
      setHtmlContent(previewHtml);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const html = await incomeExpenseApi.fetchLedgerVoucherHtml(entryId);
        if (!cancelled) setHtmlContent(html);
      } catch {
        if (!cancelled) setHtmlContent(previewHtml);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [entryId, previewHtml]);

  return (
    <div className="border-b">
      <div className="px-4 py-2 text-sm font-medium text-gray-800 bg-gray-50">{title}</div>
      <div className="relative bg-gray-100" style={{ minHeight: '60vh' }}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm">
            Đang tải phiếu...
          </div>
        )}
        {htmlContent ? (
          <iframe
            title={title}
            srcDoc={htmlContent}
            className="w-full border-0"
            style={{ minHeight: '60vh' }}
          />
        ) : null}
      </div>
    </div>
  );
};

export interface ActivityVoucherPrefill {
  amount: number;
  actualDate: string;
  reason?: string;
  name?: string;
  projectBankAccountId?: string;
  categoryId?: string;
}

interface CreateActivityVoucherModalProps {
  kind: 'revenue' | 'expenditure';
  buildingId: string;
  onClose: () => void;
  onCreated: (result: CreateActivityVoucherResponse) => void;
  editLedgerEntryId?: string;
  onUpdated?: () => void;
  prefill?: ActivityVoucherPrefill;
}

export const CreateActivityVoucherModal: React.FC<CreateActivityVoucherModalProps> = ({
  kind,
  buildingId,
  onClose,
  onCreated,
  editLedgerEntryId,
  onUpdated,
  prefill,
}) => {
  const isEditMode = Boolean(editLedgerEntryId);
  const isRevenue = kind === 'revenue';
  const [step, setStep] = useState<ModalStep>('form');
  const [categories, setCategories] = useState<IeCategory[]>([]);
  const [bankAccounts, setBankAccounts] = useState<ProjectBankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [createdResult, setCreatedResult] = useState<CreateActivityVoucherResponse | null>(null);
  const [categoryId, setCategoryId] = useState('');
  const [amountInput, setAmountInput] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'CASH' | 'BANK_TRANSFER'>('BANK_TRANSFER');
  const [actualDate, setActualDate] = useState(toDateInputValue(new Date()));
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [counterpartyName, setCounterpartyName] = useState('');
  const [apartmentSearch, setApartmentSearch] = useState('');
  const [apartmentId, setApartmentId] = useState('');
  const [linkedApartment, setLinkedApartment] = useState<{
    id: string;
    code: string;
    name?: string | null;
  } | null>(null);
  const [apartmentOptions, setApartmentOptions] = useState<Array<{ id: string; code: string; name?: string | null }>>([]);
  const [editingVoucherId, setEditingVoucherId] = useState<string | null>(null);
  const [projectBankAccountId, setProjectBankAccountId] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [categoryRows, accountRows] = await Promise.all([
          isRevenue
            ? incomeExpenseApi.listRevenueCategories(buildingId)
            : incomeExpenseApi.listExpenditureCategories(buildingId),
          incomeExpenseApi.listProjectBankAccounts(buildingId),
        ]);
        const blocked = isRevenue ? BLOCKED_REVENUE_CODES : BLOCKED_EXPENDITURE_CODES;
        const selectable = (categoryRows || []).filter(
          (cat) => cat.isActive && !blocked.has(cat.code),
        );
        setCategories(selectable);
        setBankAccounts(accountRows || []);
        if (!isEditMode && prefill) {
          setAmountInput(formatAmountInput(prefill.amount));
          setActualDate(prefill.actualDate);
          setPaymentMethod('BANK_TRANSFER');
          if (prefill.reason) setReason(prefill.reason);
          if (prefill.name) setName(prefill.name);
          if (prefill.projectBankAccountId) setProjectBankAccountId(prefill.projectBankAccountId);
          const preferredCategory =
            (prefill.categoryId && selectable.find((c) => c.id === prefill.categoryId)) ||
            selectable.find((c) => /khác/i.test(c.name)) ||
            selectable[0];
          if (preferredCategory) setCategoryId(preferredCategory.id);
        } else if (!isEditMode && selectable.length > 0) {
          setCategoryId(selectable[0].id);
        }

        if (isEditMode && editLedgerEntryId) {
          const editForm = isRevenue
            ? await incomeExpenseApi.getRevenueVoucherForEdit(editLedgerEntryId)
            : await incomeExpenseApi.getExpenditureVoucherForEdit(editLedgerEntryId);
          setEditingVoucherId(editForm.voucherId);
          if (editForm.categoryId) setCategoryId(editForm.categoryId);
          setAmountInput(formatAmountInput(editForm.amount));
          setPaymentMethod(editForm.paymentMethod);
          setActualDate(toDateInputValue(new Date(editForm.actualDate)));
          setName(editForm.name || '');
          setReason(editForm.reason || '');
          setCounterpartyName(editForm.counterpartyName || '');
          if (editForm.apartmentId && editForm.apartmentCode) {
            setApartmentId(editForm.apartmentId);
            setLinkedApartment({
              id: editForm.apartmentId,
              code: editForm.apartmentCode,
              name: editForm.apartmentName,
            });
            setApartmentSearch(editForm.apartmentCode);
          } else {
            setApartmentId('');
            setLinkedApartment(null);
            setApartmentSearch(editForm.apartmentLabel || '');
          }
          if (editForm.projectBankAccountId) {
            setProjectBankAccountId(editForm.projectBankAccountId);
          }
        }
      } catch {
        toast.error(isEditMode ? 'Không thể tải phiếu để chỉnh sửa' : 'Không thể tải dữ liệu tạo phiếu');
      } finally {
        setLoading(false);
      }
    };
    if (buildingId) load();
  }, [buildingId, isRevenue, isEditMode, editLedgerEntryId, prefill]);

  useEffect(() => {
    if (!buildingId || apartmentSearch.trim().length < 2) {
      setApartmentOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const list = await paymentApi.searchApartmentMatches(buildingId, apartmentSearch.trim());
        setApartmentOptions(list);
        if (list.length === 1) {
          const apt = list[0];
          setApartmentId(apt.id);
          setLinkedApartment(apt);
        }
      } catch {
        setApartmentOptions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [apartmentSearch, buildingId]);

  const selectedCategory = categories.find((cat) => cat.id === categoryId) || null;

  const staffSelectsProjectBank = selectedCategory?.projectBankAccountMode === 'STAFF_SELECT';

  const defaultBankAccount = useMemo(
    () => bankAccounts.find((account) => account.isDefault) || bankAccounts[0],
    [bankAccounts],
  );

  useEffect(() => {
    if (!staffSelectsProjectBank) return;
    if (projectBankAccountId) return;
    if (defaultBankAccount) setProjectBankAccountId(defaultBankAccount.id);
  }, [staffSelectsProjectBank, defaultBankAccount, projectBankAccountId]);

  const resolvedAccountLabel = useMemo(() => {
    if (!selectedCategory) return '—';
    if (selectedCategory.projectBankAccountMode === 'STAFF_SELECT') {
      const account = bankAccounts.find((row) => row.id === projectBankAccountId);
      return account ? formatAccountLabel(account) : 'Chọn tài khoản';
    }
    if (
      selectedCategory.projectBankAccountMode === 'FIXED_ACCOUNT' &&
      selectedCategory.projectBankAccount
    ) {
      return formatAccountLabel(selectedCategory.projectBankAccount);
    }
    return defaultBankAccount
      ? `${formatAccountLabel(defaultBankAccount)} (mặc định)`
      : 'Chưa có tài khoản dự án';
  }, [bankAccounts, defaultBankAccount, projectBankAccountId, selectedCategory]);

  const buildPayload = (): CreateActivityVoucherPayload | null => {
    if (!categoryId) {
      toast.error('Vui lòng chọn loại phiếu');
      return null;
    }
    const amount = parseAmountInput(amountInput);
    if (amount <= 0) {
      toast.error('Vui lòng nhập số tiền hợp lệ');
      return null;
    }
    if (staffSelectsProjectBank && !projectBankAccountId) {
      toast.error('Vui lòng chọn tài khoản dự án');
      return null;
    }
    const apartmentText = apartmentSearch.trim();
    return {
      buildingId,
      categoryId,
      amount,
      paymentMethod,
      actualDate,
      apartmentId: apartmentId || undefined,
      apartmentLabel: !apartmentId && apartmentText ? apartmentText : undefined,
      name: name.trim() || undefined,
      reason: reason.trim() || undefined,
      counterpartyName: counterpartyName.trim() || undefined,
      ...(staffSelectsProjectBank ? { projectBankAccountId } : {}),
    };
  };

  const handlePreview = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setPreviewLoading(true);
    try {
      const result = isRevenue
        ? await incomeExpenseApi.previewRevenueVoucher(payload)
        : await incomeExpenseApi.previewExpenditureVoucher(payload);
      setPreviewHtml(result.html);
      setStep('preview');
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Không thể xem trước phiếu'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirm = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setConfirmLoading(true);
    try {
      if (isEditMode && editingVoucherId) {
        const result = isRevenue
          ? await incomeExpenseApi.updateRevenueVoucher(editingVoucherId, payload)
          : await incomeExpenseApi.updateExpenditureVoucher(editingVoucherId, payload);
        toast.success(`Đã cập nhật phiếu ${result.journalNumber}`);
        onUpdated?.();
        onClose();
        return;
      }
      const result = isRevenue
        ? await incomeExpenseApi.createRevenueVoucher(payload)
        : await incomeExpenseApi.createExpenditureVoucher(payload);
      setCreatedResult(result);
      setStep('success');
      toast.success(`Đã tạo phiếu ${result.journalNumber}`);
      onCreated(result);
    } catch (error: unknown) {
      toast.error(
        getApiErrorMessage(error, isEditMode ? 'Không thể cập nhật phiếu' : 'Không thể tạo phiếu'),
      );
    } finally {
      setConfirmLoading(false);
    }
  };

  const title = isEditMode
    ? isRevenue
      ? 'Chỉnh sửa phiếu thu'
      : 'Chỉnh sửa phiếu chi'
    : isRevenue
      ? 'Tạo phiếu thu'
      : 'Tạo phiếu chi';
  const categoryLabel = isRevenue ? 'Loại phiếu thu' : 'Loại phiếu chi';
  const dateLabel = isRevenue ? 'Ngày thực thu' : 'Ngày thực chi';
  const counterpartyLabel = isRevenue ? 'Tên người nộp' : 'Tên người nhận';
  const apartmentLabel = isRevenue ? 'Căn hộ thu (tuỳ chọn)' : 'Căn hộ chi (tuỳ chọn)';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto flex flex-col">
        <div className="border-b px-6 py-4 flex items-center justify-between shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl leading-none">
            ×
          </button>
        </div>

        {step === 'form' && (
          <>
            {loading ? (
              <div className="p-8 text-center text-gray-500">Đang tải...</div>
            ) : (
              <div className="p-6 space-y-4">
                <div className="text-sm text-cyan-800 bg-cyan-50 border border-cyan-100 rounded-lg px-4 py-3">
                  {staffSelectsProjectBank
                    ? 'Loại phiếu này không gán tài khoản cố định — chọn tài khoản dự án khi tạo phiếu.'
                    : 'Tài khoản dự án theo cấu hình loại phiếu (mặc định hoặc gán cố định).'}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">{categoryLabel}</label>
                    <select
                      value={categoryId}
                      onChange={(e) => {
                        const nextId = e.target.value;
                        const nextCategory = categories.find((cat) => cat.id === nextId);
                        setCategoryId(nextId);
                        if (
                          nextCategory?.projectBankAccountMode === 'FIXED_ACCOUNT' &&
                          nextCategory.projectBankAccount
                        ) {
                          setProjectBankAccountId(nextCategory.projectBankAccount.id);
                        } else if (nextCategory?.projectBankAccountMode === 'STAFF_SELECT') {
                          if (!projectBankAccountId && defaultBankAccount) {
                            setProjectBankAccountId(defaultBankAccount.id);
                          }
                        }
                      }}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    >
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.code} — {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Tài khoản dự án</label>
                    {staffSelectsProjectBank ? (
                      <select
                        value={projectBankAccountId}
                        onChange={(e) => setProjectBankAccountId(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      >
                        <option value="">Chọn tài khoản</option>
                        {bankAccounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            {formatAccountLabel(account)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        {resolvedAccountLabel}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Số tiền</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={amountInput}
                      onChange={(e) => setAmountInput(e.target.value)}
                      onBlur={() => setAmountInput(formatAmountInput(parseAmountInput(amountInput)))}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Hình thức thanh toán</label>
                    <select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value as 'CASH' | 'BANK_TRANSFER')}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    >
                      <option value="BANK_TRANSFER">Chuyển khoản</option>
                      <option value="CASH">Tiền mặt</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">{dateLabel}</label>
                    <input
                      type="date"
                      value={actualDate}
                      onChange={(e) => setActualDate(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Tên phiếu</label>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={selectedCategory?.name || ''}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-700 mb-1">Lý do</label>
                    <input
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      placeholder="Mô tả ngắn gọn"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">{counterpartyLabel}</label>
                    <input
                      value={counterpartyName}
                      onChange={(e) => setCounterpartyName(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">{apartmentLabel}</label>
                    {linkedApartment && apartmentId === linkedApartment.id ? (
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-emerald-800">Đã chọn căn hộ trên hệ thống</p>
                            <p className="mt-0.5 text-sm font-semibold text-emerald-950 truncate">
                              {linkedApartment.code}
                              {linkedApartment.name ? (
                                <span className="font-normal text-emerald-900"> — {linkedApartment.name}</span>
                              ) : null}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setApartmentId('');
                              setLinkedApartment(null);
                              setApartmentSearch('');
                              setApartmentOptions([]);
                            }}
                            className="shrink-0 text-xs font-medium text-emerald-800 hover:text-emerald-950 underline"
                          >
                            Đổi
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <input
                          value={apartmentSearch}
                          onChange={(e) => {
                            setApartmentSearch(e.target.value);
                            setApartmentId('');
                            setLinkedApartment(null);
                          }}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                          placeholder="Mã căn hộ hoặc mô tả khách ngoài tòa nhà"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Chọn căn hộ trong gợi ý nếu có trên hệ thống; nếu không, nhập mô tả và lưu phiếu (không
                          cần chọn).
                        </p>
                        {!apartmentId && apartmentSearch.trim().length >= 2 && apartmentOptions.length === 0 && (
                          <p className="mt-1 text-xs text-amber-700">
                            Không tìm thấy căn hộ — sẽ lưu nội dung bạn nhập trên phiếu.
                          </p>
                        )}
                        {apartmentOptions.length > 0 && (
                          <div className="mt-1 border rounded-lg bg-white shadow-sm max-h-40 overflow-y-auto">
                            {apartmentOptions.map((apt) => (
                              <button
                                key={apt.id}
                                type="button"
                                onClick={() => {
                                  setApartmentId(apt.id);
                                  setLinkedApartment(apt);
                                  setApartmentSearch(apt.code);
                                  setApartmentOptions([]);
                                }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-cyan-50"
                              >
                                {apt.code}{apt.name ? ` — ${apt.name}` : ''}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="border-t px-6 py-4 flex justify-end gap-3 bg-gray-50 rounded-b-lg shrink-0">
              <button
                type="button"
                onClick={onClose}
                disabled={previewLoading}
                className="px-5 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-50"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handlePreview}
                disabled={previewLoading || loading || categories.length === 0}
                className="px-5 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium disabled:opacity-50"
              >
                {previewLoading ? 'Đang tải...' : 'Xem trước phiếu'}
              </button>
            </div>
          </>
        )}

        {step === 'preview' && (
          <>
            <LedgerVoucherPane entryId={null} previewHtml={previewHtml} title={`${title} (xem trước)`} />
            <div className="border-t px-6 py-4 flex justify-between items-center gap-3 bg-gray-50 shrink-0">
              <button
                type="button"
                onClick={() => setStep('form')}
                disabled={confirmLoading}
                className="px-5 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-white"
              >
                Quay lại
              </button>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => previewHtml && printVoucherHtml(previewHtml)}
                  className="px-5 py-2 rounded-lg border border-cyan-200 text-sm font-medium text-cyan-700 hover:bg-cyan-50"
                >
                  In xem trước
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={confirmLoading}
                  className="px-5 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium disabled:opacity-50"
                >
                  {confirmLoading ? 'Đang xử lý...' : 'Xác nhận'}
                </button>
              </div>
            </div>
          </>
        )}

        {step === 'success' && createdResult && (
          <>
            <div className="px-6 py-2 bg-green-50 border-b border-green-100 text-sm text-green-800">
              Đã ghi sổ: {createdResult.journalNumber}
            </div>
            <LedgerVoucherPane
              entryId={createdResult.activityLedgerEntryId}
              previewHtml={previewHtml}
              title={title}
            />
            <div className="border-t px-6 py-4 flex justify-end gap-3 bg-gray-50 shrink-0">
              <button
                type="button"
                onClick={async () => {
                  try {
                    const html = await incomeExpenseApi.fetchLedgerVoucherHtml(
                      createdResult.activityLedgerEntryId,
                    );
                    printVoucherHtml(html);
                  } catch {
                    toast.error('Không thể in phiếu');
                  }
                }}
                className="px-5 py-2 rounded-lg border border-cyan-200 text-sm font-medium text-cyan-700 hover:bg-cyan-50"
              >
                In phiếu
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium"
              >
                Đóng
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CreateActivityVoucherModal;
